define((require) => {
    "use strict";

    const _             = require("underscore");
    const Control       = require("ui/experience/widgets/transformations/Control");
    const Controls      = require("ui/experience/widgets/transformations/Controls");
    const TElement      = require("ui/experience/widgets/transformations/TElement");
    const TGroup        = require("ui/experience/widgets/transformations/TGroup");
    const Marionette    = require("marionette");
    const Preconditions = require("shared/util/Preconditions");
    const Mouse         = require("ui/experience/widgets/transformations/Mouse");

    /**
     * FreeTransform class manages transforming dom elements and dispatches relevant events. This
     * class is the main API that interact with other components, use this class if you need to
     * access this FreeTransform component.
     *
     * @class FreeTransform
     */
    const FreeTransform = Marionette.Controller.extend(
        {
        // A flag to indicate whether an element has been dragged during mouse events.
            dragged: false,
            reselect: false,
            _coordinatesOnMouseDown: null,

            /**
         * @type Layer
         */
            _layer: null,

            /**
         * @type Scene
         */
            _scene: null,

            viewModel: null,

            constructor(attrs) {
                Preconditions.checkArguments(attrs.layer);
                Preconditions.checkArguments(attrs.scene);
                Preconditions.checkArguments(attrs.viewModel);

                this._layer = attrs.layer;
                this._scene = attrs.scene;
                this.viewModel = attrs.viewModel;

                // A list of elements can be transformed.
                this.transformables = [];
                this._bindHandlers();

                this.cid = _.uniqueId("c");
            },

            _bindHandlers() {
                this.up = this.up.bind(this);
                this.down = this.down.bind(this);
                this.move = this.move.bind(this);
            },

            setContainer(container) {
                this.container = container.jquery ? container[0] : container;
                this.controlsContainer = document.createElement("div");

                // No-scale class indicates that the container should be counter scaled in zooming mode.
                this.controlsContainer.className = "controls-container no-scale";
                this.container.addEventListener(Mouse.START, this.down);
                this.container.appendChild(this.controlsContainer);

                if (!this.container) {
                    throw new Error("Container must be set for the FreeTransform");
                }

                this.controls = new Controls(this.controlsContainer);
            },

            /**
         * Add a transformable element to the list, so it can be transformed.
         *
         * @param {Object} ele  The dom element.
         * @param {Object} data Any data that users want to pass in, it will be accessible from
         *                      the data property of elem.
         */
            add(ele, data) {
                this.transformables.push(new TElement(ele, data));
            },

            /**
         * Remove the transformable element from the transformable list, call this when the
         * element is removed from the dom.
         *
         * @param {Object} ele The dom element to be removed.
         */
            remove(ele) {
                let t = null;
                this.transformables.forEach((t, i) => {
                    if (t.domElem === ele) {
                        t = this.transformables.splice(i, 1);
                    }
                });

                if (this.controls.targetIs(t)) {
                    this.controls.removeTarget();
                }
            },

            /**
         * An event trigger portal.
         *
         * @param {Event} event The event name (@see events).
         */
            triggerEvent(event) {
                const mainTarget = this.controls.target;
                let targets = [];

                if (mainTarget instanceof TGroup) {
                    targets = mainTarget.members;
                } else if (mainTarget instanceof TElement) {
                    targets.push(mainTarget);
                }

                this.trigger(event, targets, this, mainTarget);
            },

            getTargetWidget() {
                return this.controls.target && this.controls.target.data && this.controls.target.data.widget ? this.controls.target.data.widget : null;
            },

            /**
         * Do some initial work when mouse is down: selecting a target and add move event
         * listener etc.
         *
         * @param {Event} event
         */
            down(event) {
            // TODO: replace this hack with something a bit more programatic.
                if (!this.viewModel.get("preventSelectionChange")) {
                // Classnames of Elements are not always strings. Occasionally they are an SVGAnimatedString.
                // This happens if the element is and SVGElement.
                // See Here: https://developer.mozilla.org/en-US/docs/Web/API/Element/className
                    if (event.target instanceof SVGElement || event.target.className.indexOf("image-crop") === -1) {
                        Mouse.get(event, this.container);

                        const t = this._findTopMostElem(Mouse.x, Mouse.y, event);

                        const target = this.controls.target;
                        const modifier = event.shiftKey || event.ctrlKey || event.metaKey;

                        if (target instanceof TElement && t !== target) {
                        // We need to trigger this before updateTarget call, otherwise target will be null.
                            this.triggerEvent(FreeTransform.TARGET_DESELECTED);
                        }

                        this.controls.updateTarget(t, modifier);

                        if (t) {
                            document.addEventListener(Mouse.MOVE, this.move);
                            document.addEventListener(Mouse.END, this.up);

                            this.controls.start(Mouse.x, Mouse.y);
                            this.reselect = (t === target);

                            if (!this.reselect) {
                                this.triggerEvent(FreeTransform.TARGET_SELECTED);
                            }
                        }

                        this.triggerEvent(FreeTransform.MOUSEDOWN);
                        this.dragged = false;
                        // Correct (compensate) the coordinates, if zoom is used
                        const zoom = this.viewModel.get("zoomModel");

                        this._coordinatesOnMouseDown = {
                            x: Mouse.x / zoom.get("scale"),
                            y: Mouse.y / zoom.get("scale")
                        };
                    }
                    const targetWidget = this.getTargetWidget();
                    const textWidget = targetWidget && targetWidget.model.get("type") === "text-enhanced" ? targetWidget : null;
                    // Don't move the widget if the mouse is dragging inside the widget to allow for text selection.
                    if (textWidget) {
                        if (event.target && !event.target.classList.contains("drag-container")) {
                            if (targetWidget.model.get("isDraggable") === true) {
                                targetWidget.model.set("isDraggable", false);
                            }
                        }
                    }
                }
            },

            move(event) {
            // Don't move the widget if the mouse is moving too far away from the scene.
                if (this._isMouseOutsideBoundingBox(event.clientX, event.clientY)) {
                    return;
                }

                const targetWidget = this.getTargetWidget();
                const isDragEvent = this.controls.control.type === "target" || this.controls.control.type === "edgeTranslate";
                // Dragging/Scaling/Rotating/Target are all considered "move" events.
                // "target" is a move event produced by beginning the drag inside the widget (selecting text) that we block if the widget isn't draggable.
                if (isDragEvent && (targetWidget && targetWidget.model.get("isDraggable") === false)) {
                    return;
                }

                Mouse.get(event, this.container);

                // Don't trigger a move if the cursor wasn't moved from the position when mouse
                // down was triggered. This is a workaround for this bug in Chrome:
                // https://code.google.com/p/chromium/issues/detail?id=161464
                // See also TransformTextWidget.js.
                if (this._coordinatesOnMouseDown !== null &&
                Mouse.x === this._coordinatesOnMouseDown.x &&
                Mouse.y === this._coordinatesOnMouseDown.y) {
                // Don't check the coordiantes once the cursor has been moved. We still want
                // the move event if the cursor is moved back to the original position.
                    this._coordinatesOnMouseDown = null;

                    return;
                }

                this.controls.move(Mouse.x, Mouse.y);
                // Fire some events.
                switch (this.controls.control.type) {
                    case Control.EDGE_TRANSLATE:
                    case Control.TARGET:
                        this.triggerEvent(FreeTransform.TARGET_TRANSLATING);
                        break;

                    case Control.ROTATE:
                        this.triggerEvent(FreeTransform.TARGET_ROTATING);
                        break;

                    default:
                        this.triggerEvent(FreeTransform.TARGET_SCALING);
                }
                this.triggerEvent(FreeTransform.TARGET_TRANSFORMING);

                this.dragged = true;
                event.preventDefault();
            },

            setBoundingBoxProvider(providerFunction) {
                Preconditions.isFunction(providerFunction);
                this._viewportBoundingBoxProvider = providerFunction;
            },

            /**
         * @private
         *
         * Whether the specified coordinates are outside the current bounding box.
         *
         * @param  {Number}  mouseX The horizontal coordinate within the application's client
         *                          area at which the event occurred.
         *
         * @param  {Number}  mouseY The vertical coordinate within the application's client
         *                          area at which the event occurred.
         *
         * @return {Boolean}        <tt>true</tt> if the specified coordinates are outside the
         *                          current bounding box, <tt>false</tt> if not.
         */
            _isMouseOutsideBoundingBox(mouseX, mouseY) {
                if (!this._viewportBoundingBoxProvider) {
                    return false;
                }

                const bounds = this._viewportBoundingBoxProvider();

                return mouseX < bounds.left || mouseX > bounds.right || mouseY < bounds.top || mouseY > bounds.bottom;
            },

            up(event) {
                document.removeEventListener(Mouse.MOVE, this.move);
                document.removeEventListener(Mouse.END, this.up);

                if (this.dragged) {
                // Only the mouse has been moved, can we safely assume that the target has been transformed.
                    this.triggerEvent(FreeTransform.TARGET_TRANSFORMING_END);
                } else if (this.reselect) {
                    this.triggerEvent(FreeTransform.TARGET_RESELECTED);
                }

                if (this.controls.isTransformed()) {
                    this.triggerEvent(FreeTransform.TARGET_TRANSFORMED);
                }

                const targetWidget = this.getTargetWidget();
                if (targetWidget && targetWidget.model.get("isDraggable") === false) {
                    targetWidget.model.set("isDraggable", true);
                }

                this.controls.end();
                event.preventDefault();
            },

            /**
         * Select a/multiple element(s).
         *
         * @param {Object} elems transformable elements {@link TElement} or dom elements.
         */
            select(elems) {
                elems = _.isArray(elems) ? elems : [elems];

                // map to transformable elements if they are raw dom elements, else return itself.
                const transformables = _.map(elems, (elem) => {
                    if (this._isDomElement(elem)) {
                        elem = this._findByDomElem(elem);
                    }

                    return elem;
                });

                const newTarget = _.compact(transformables).length > 1 ? new TGroup(transformables) : transformables[0];
                if (newTarget) {
                    this.controls.setTarget(newTarget);

                    this.triggerEvent(FreeTransform.TARGET_SELECTED);
                }
            },

            /**
         * Find a transformable element by a dom element.
         *
         * @param {Object} elem
         * @returns {TElement} Transformable element if found, otherwise undefined
         */
            _findByDomElem(elem) {
                return _.find(this.transformables, (tran) => tran.domElem === elem);
            },

            /**
         * Find a transformable element by a widget uuid.
         *
         * @param {String} uuid
         * @returns {TElement} Transformable element if found, otherwise undefined
         */
            _findByUuid(uuid) {
                // check widgets in selecting layer for a uuid match and return the dom element from findByDomElem
                return _.find(this.transformables, (tran) => {
                    return tran.data &&
                     tran.data.widget &&
                     tran.data.widget.model.get("uuid") === uuid;
                });
            },

            /**
         * Find the transformable element with highest z-index at point x and y.
         *
         * @param {Number} x X-coordinate relative to the scene.
         * @param {Number} y Y-coordinate relative to the scene.
         * @returns the {@link Transformable}transformable element
         */
            _findTopMostElem(x, y, event) {
                let currentlyChosenElement = null;
                const scale  = this.controls.scale;
                const target = this.controls.target;

                if (this._layer !== this.viewModel.get("currentLayer")) {
                    return null; // widget on different layer to active layer.
                }

                if (parseInt(this._scene.get("sceneNumber"), 10) !== parseInt(this.viewModel.get("sceneNumber"), 10)) {
                    return null; // widget on different scene to active scene.
                }

                // If a non-translate control on the currently selected target is clicked, treat it
                // as topmost.
                if (target && this.controls.getControlAt(x, y)) {
                    return target;
                }

                this.transformables
                    // do not consider locked widgets as selectable elements
                    .filter((element) => !element.data.widget.model.get("isLocked"))
                    .forEach((element) => {
                        const elementPositionMatrix = element.getMatrixWithParentOffset();
                        elementPositionMatrix.x *= scale;
                        elementPositionMatrix.y *= scale;

                        if (elementPositionMatrix.containsPoint(x, y, element.getWidth() * scale, element.getHeight() * scale)) {
                            if (this._isTopmostElementOfGroup(currentlyChosenElement, element)) {
                                currentlyChosenElement = element;
                            }
                        } else if (this._hasTab(element)) {
                            const {yOffset, width, height} = element.data.widget.options.tabProperties;
                            if (elementPositionMatrix.containsPoint(x, y + yOffset, width, height)) {
                                if (this._isTopmostElementOfGroup(currentlyChosenElement, element)) {
                                    currentlyChosenElement = element;
                                }
                            }
                        } else if (!currentlyChosenElement) {
                            const targetElement = document.elementFromPoint(event.clientX, event.clientY);
                            if (targetElement.classList.contains("drag-container")) {
                                const containedElement = this._findByUuid(targetElement.dataset.containedWidgetUuid);
                                if (this._isTopmostElementOfGroup(currentlyChosenElement, containedElement)) {
                                    currentlyChosenElement = containedElement;
                                }
                            }
                        }
                    });

                return currentlyChosenElement;
            },

            // Widgets have a property on them that answers if it has a tab or not
            _hasTab(element) {
                return element.data.widget.options.hasTab;
            },

            _isTopmostElementOfGroup(currentElement, element) {
                return !currentElement || currentElement.getZIndex() < element.getZIndex();
            },

            /**
         * Check if an element is a raw dom element.
         *
         * @param {Object }elem
         * @returns {Boolean} true if it is a dom element, false otherwise.
         */
            _isDomElement(elem) {
                return (
                    typeof HTMLElement === "object" ? elem instanceof HTMLElement : // DOM2.
                        elem && typeof elem === "object" && elem !== null &&
                            elem.nodeType === 1 && typeof elem.nodeName === "string"
                );
            },

            // Update controls to reflect any changes to the elements call this when the target is
            // transformed/modified externally.
            updateControls(scale) {
                if (this.controls) {
                    this.controls.updateFromTarget(scale);
                }
            },

            // Hide all the controls
            hideControls() {
                this.controls.hide();
            },

            setClass(value) {
                this.controls.setControlClass(value);
            },

            removeClass(value) {
                if (this.controls) {
                    this.controls.removeControlClass(value);
                }
            },

            hasTarget() {
                return this.controls.hasTarget();
            },

            deselect() {
                this.triggerEvent(FreeTransform.TARGET_DESELECTED);
                this.controls.removeTarget();
            },

            /**
         * Move/translate the current target by x and y
         *
         * @param {Number} x
         * @param {Number} y
         */
            translate(x, y) {
                if (this.controls.target) {
                    this.controls.translate(x, y);
                    this.triggerEvent(FreeTransform.TARGET_TRANSFORMED);
                }
            }

        },
        {
            TARGET_SELECTED: "select_target",
            TARGET_DESELECTED: "deselect_target",
            TARGET_RESELECTED: "reselect_target",
            TARGET_TRANSFORMED: "target_transformed",
            TARGET_TRANSFORMING: "transforming",
            TARGET_TRANSFORMING_END: "transforming_end",
            TARGET_TRANSLATING: "translating",
            TARGET_ROTATING: "rotating",
            TARGET_SCALING: "scaling",
            MOUSEDOWN: "mouse_down"
        }
    );

    return FreeTransform;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/transformations/FreeTransform.js